import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useCurrentStore } from '@/hooks/useCurrentStore';

interface SalesData {
  date: string;
  revenue: number;
  orders: number;
}

interface SalesChartProps {
  data: SalesData[];
}

export function SalesChart({ data }: SalesChartProps) {
  const { currentStore } = useCurrentStore();

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currentStore?.currency || 'NGN',
    }).format(amount);

  return (
    <div className="h-[250px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
        >
          <defs>
            <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#451892" stopOpacity={0.28} />
              <stop offset="95%" stopColor="#451892" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="ordersGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#be9afc" stopOpacity={0.35} />
              <stop offset="95%" stopColor="#be9afc" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#efece6" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(v) =>
              new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            }
            fontSize={10}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            tick={{ fill: '#9ca3af' }}
          />
          <YAxis
            yAxisId="left"
            tickFormatter={(v: number) =>
              v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
            }
            fontSize={10}
            tickLine={false}
            axisLine={false}
            tick={{ fill: '#9ca3af' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            fontSize={10}
            tickLine={false}
            axisLine={false}
            tick={{ fill: '#9ca3af' }}
          />
          <Tooltip
            formatter={(value: number, name: string) => [
              name === 'revenue' ? formatCurrency(value) : value,
              name.charAt(0).toUpperCase() + name.slice(1),
            ]}
            labelFormatter={(label) =>
              new Date(label).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
              })
            }
            contentStyle={{
              fontSize: '12px',
              borderRadius: '16px',
              border: '1px solid rgba(69,24,146,0.08)',
              boxShadow: '0 16px 40px -24px rgba(69,24,146,0.2)',
            }}
          />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="revenue"
            stroke="#451892"
            strokeWidth={2}
            fill="url(#revenueGrad)"
            dot={false}
            activeDot={{ r: 4, fill: '#451892' }}
          />
          <Area
            yAxisId="right"
            type="monotone"
            dataKey="orders"
            stroke="#be9afc"
            strokeWidth={2}
            fill="url(#ordersGrad)"
            dot={false}
            activeDot={{ r: 4, fill: '#be9afc' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
